import { Pressable, StyleSheet, Text, View } from 'react-native';
import {
  formatRestTime,
  formatWeight,
  type WorkoutExerciseEntry,
} from '@jackedlogs/shared';
import { Icon } from '../index';

import type { useTheme } from './theme';
import type { WeightUnit } from './state/loggerDraft';
import { formatShortDate } from './formatDate';
import { machineLabel } from './machineLabel';
import { useEntryHistory, type HistorySession } from './useEntryHistory';

// Collapsible panel under the entry header. Renders nothing when the
// EntryHistoryProvider knows the slug has no past sessions — Antenore's
// rule: "no storico, no render". When history exists the user gets up
// to 3 recent rows plus the all-time best-e1RM row (only when the best
// session isn't already in the recent window). Each row carries its own
// Copia CTA; the parent owns the append-vs-overwrite popup logic.

type Theme = ReturnType<typeof useTheme>;

type Props = {
  exerciseId: string;
  onCopy: (sourceEntry: WorkoutExerciseEntry) => void;
  locale: string;
  unit: WeightUnit;
  theme: Theme;
  getExerciseNameBySlug: (slug: string, fallback?: string) => string;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

export function HistoryPanel({
  exerciseId,
  onCopy,
  locale,
  unit,
  theme,
  getExerciseNameBySlug,
  t,
}: Props) {
  const { data } = useEntryHistory(exerciseId);
  if (data === null) return null;

  const styles = makeStyles(theme);
  const hasRealHistory = data.recent.length > 0 || data.best !== null;

  // Slice 4.2: panel renders only when the entry's 🕐 chip is active
  // (parent gate). Internal collapse/expand state removed — the chip
  // is the single toggle. Header keeps the label + count for context;
  // tapping it elsewhere does nothing now.
  if (hasRealHistory) {
    const totalRows = data.recent.length + (data.best ? 1 : 0);
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerLabel}>
            {t('mobile.logger.historyToggle', { defaultValue: 'History' })}
          </Text>
          <Text style={styles.headerCount}>
            {t('mobile.logger.historyCount', {
              defaultValue: '{{n}} sessions',
              n: totalRows,
              count: totalRows,
            })}
          </Text>
        </View>
        <View style={styles.body}>
          {data.recent.map((s) => (
            <HistoryRow
              key={s.sessionId}
              session={s}
              isBest={false}
              locale={locale}
              unit={unit}
              styles={styles}
              theme={theme}
              t={t}
              onCopy={onCopy}
            />
          ))}
          {data.best ? (
            <HistoryRow
              session={data.best}
              isBest={true}
              locale={locale}
              unit={unit}
              styles={styles}
              theme={theme}
              t={t}
              onCopy={onCopy}
            />
          ) : null}
        </View>
      </View>
    );
  }

  // No history of its own. Offer an estimate from the most-similar
  // exercises that DO have history (Antenore: let the user pick the
  // source, or say clearly there's nothing to estimate from). Copy
  // routes through the same add/replace popup as a real history row.
  const candidates = data.estimate?.candidates ?? [];
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerLabel}>
          {t('mobile.logger.historyEstimateHeader', {
            defaultValue: 'Estimate from a similar exercise',
          })}
        </Text>
      </View>
      <View style={styles.body}>
        {candidates.length === 0 ? (
          <Text style={styles.emptyMsg}>
            {t('mobile.logger.historyNoEstimate', {
              defaultValue:
                'No history yet, and no similar exercise to estimate from.',
            })}
          </Text>
        ) : (
          candidates.map((c) => (
            <HistoryRow
              key={c.exerciseId}
              session={c.session}
              isBest={false}
              sourceName={getExerciseNameBySlug(c.exerciseId)}
              locale={locale}
              unit={unit}
              styles={styles}
              theme={theme}
              t={t}
              onCopy={onCopy}
            />
          ))
        )}
      </View>
    </View>
  );
}

type RowProps = {
  session: HistorySession;
  isBest: boolean;
  locale: string;
  unit: WeightUnit;
  styles: ReturnType<typeof makeStyles>;
  theme: Theme;
  t: Props['t'];
  onCopy: Props['onCopy'];
  // When set, this row is an estimate from a similar exercise — show its
  // name so the user knows the proposed set isn't their own history.
  sourceName?: string;
};

function HistoryRow({
  session,
  isBest,
  locale,
  unit,
  styles,
  theme,
  t,
  onCopy,
  sourceName,
}: RowProps) {
  return (
    <View style={[styles.row, isBest && styles.rowBest]}>
      <View style={styles.rowMain}>
        {sourceName ? (
          <Text style={styles.sourceName} numberOfLines={1}>
            {t('mobile.logger.historyEstimatedFrom', {
              defaultValue: 'From {{name}}',
              name: sourceName,
            })}
          </Text>
        ) : null}
        <View style={styles.rowDateLine}>
          {isBest ? (
            <Text style={styles.bestBadge}>
              {t('mobile.logger.historyBest', { defaultValue: 'Best' })}
            </Text>
          ) : null}
          <Text style={styles.rowDate}>
            {formatShortDate(session.startedAt, locale)}
          </Text>
          {/* EQ-1 — which machine that session was on. Display-only here: the
              glyph marks the identity so two machines' rows are told apart at
              a glance, and the tag is managed in exactly one place (replace
              exercise), never from a history row. */}
          {session.entry.equipmentInstance ? (
            <View style={styles.machineTag}>
              <Icon name="tag" size={11} color={theme.palette.ink3} />
              <Text style={styles.machineTagText}>
                {machineLabel(session.entry.equipmentInstance, t)}
              </Text>
            </View>
          ) : null}
        </View>
        <View style={styles.rowSets}>
          {session.entry.sets.map((s, idx) => (
            <Text
              key={`${session.sessionId}-${idx}`}
              style={styles.setChip}
              numberOfLines={1}
            >
              {setChipText(s, unit, locale, t)}
            </Text>
          ))}
        </View>
      </View>
      <Pressable
        accessibilityRole="button"
        onPress={() => onCopy(session.entry)}
        style={({ pressed }) => [
          styles.copyBtn,
          pressed && styles.copyBtnPressed,
        ]}
      >
        <Text style={styles.copyBtnText}>
          {t('mobile.logger.historyCopy', { defaultValue: 'Copy' })}
        </Text>
      </Pressable>
    </View>
  );
}

function setChipText(
  s: WorkoutExerciseEntry['sets'][number],
  unit: WeightUnit,
  locale: string,
  t: Props['t'],
): string {
  if (s.type === 'weighted') {
    return `${s.reps} × ${formatWeight(s.weight, unit, locale)}`;
  }
  if (s.type === 'bodyweight') {
    return t('mobile.logger.historyBodyweight', {
      defaultValue: '{{reps}} reps BW',
      reps: s.reps,
    });
  }
  if (s.type === 'cardio') {
    const parts: string[] = [formatRestTime(s.duration)];
    if (typeof s.distanceM === 'number' && s.distanceM > 0) {
      parts.push(
        s.distanceM >= 1000
          ? `${(s.distanceM / 1000).toFixed(2)} km`
          : `${Math.round(s.distanceM)} m`,
      );
    }
    if (typeof s.weight === 'number' && s.weight > 0) {
      parts.push(formatWeight(s.weight, unit, locale));
    }
    return parts.join(' · ');
  }
  if (s.type === 'timed') {
    // Hold/carry sets store seconds in `duration` and have no reps — without
    // this branch they fell to the fallback below and rendered "—" (inbox
    // d11620df). Mirror cardio: duration first, optional added load after.
    const parts: string[] = [formatRestTime(s.duration)];
    if (typeof s.weight === 'number' && s.weight > 0) {
      parts.push(formatWeight(s.weight, unit, locale));
    }
    return parts.join(' · ');
  }
  return `${'reps' in s && s.reps !== undefined ? s.reps : '—'}`;
}

const makeStyles = (theme: Theme) => {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    container: {
      marginTop: s.s2,
      borderRadius: r.r2,
      backgroundColor: p.surface2,
      borderWidth: 1,
      borderColor: p.border,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      gap: s.s2,
      minHeight: 44,
    },
    headerPressed: {
      opacity: 0.7,
    },
    headerLabel: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    headerCount: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      flex: 1,
    },
    headerChevron: {
      color: p.ink2,
      fontSize: ty.sizes.base,
    },
    body: {
      borderTopWidth: 1,
      borderTopColor: p.border,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      gap: s.s2,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      paddingVertical: s.s1,
    },
    rowBest: {
      borderTopWidth: 1,
      borderTopColor: p.border,
      paddingTop: s.s2,
      marginTop: s.s1,
    },
    rowMain: {
      flex: 1,
    },
    rowDateLine: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      marginBottom: 2,
    },
    bestBadge: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    sourceName: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      marginBottom: 2,
    },
    emptyMsg: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      paddingVertical: s.s1,
    },
    rowDate: {
      color: p.ink2,
      fontSize: ty.sizes.xs,
    },
    // EQ-1 — the machine marker on a history row. Glyph AND word, never the
    // glyph alone: two rows differing only by an icon would be a colour-only
    // distinction in another costume.
    machineTag: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s1,
    },
    machineTagText: {
      // `ink2`, not `ink3`: on the two light families (Latte, Dawn) ink3 lands
      // at 4.80-4.81 on surface2 — over the floor by a hair, and this reads at
      // xs. Weight and size keep it secondary; the colour buys headroom.
      color: p.ink2,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
    rowSets: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s.s1,
    },
    setChip: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontVariant: ['tabular-nums'],
      paddingHorizontal: s.s2,
      paddingVertical: 2,
      backgroundColor: p.surface,
      borderRadius: r.r1,
    },
    copyBtn: {
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      borderRadius: r.r2,
      backgroundColor: p.ink2,
      minHeight: 44,
      alignItems: 'center',
      justifyContent: 'center',
    },
    copyBtnPressed: {
      opacity: 0.7,
    },
    // The fill here is `ink2`, NOT a brand fill, so `inkOnBrand` never belonged:
    // it happened to be white in the light theme and was being read as "white".
    // DS-20 flipped that token dark and this label went to 1.87:1 — caught in
    // review, the one site out of ~50 that abused it. `bg` is the right partner
    // for an `ink2` fill in every family and mode, and paletteContrast.test.ts
    // now GUARANTEES that pair clears AA rather than leaving it to luck.
    copyBtnText: {
      color: p.bg,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
    },
  });
};
